import axios from 'axios';

const DOLIBARR_BASE_URL = 'https://accounts.firehousespirits.store/api/index.php';
const DOLAPIKEY = '7c25d121b3a046b8e51bc5961b216559641a585b';

class DolibarrAPI {
  constructor() {
    this.client = axios.create({
      baseURL: DOLIBARR_BASE_URL,
      params: {
        DOLAPIKEY
      },
      headers: {
        'Content-Type': 'application/json'
      }
    });

    // Add response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        console.error('Dolibarr API Error:', error);
        return Promise.reject(error);
      }
    );
  }

  // Products
  async getProducts() {
    try {
      const response = await this.client.get('/products', {
        params: {
          limit: 100,
          includestockdata: 1,
          includesubproducts: 1,
          includeref: 1,
          sqlfilters: '(t.tosell:=:1)'
        }
      });

      // For each product, try to get associated images
      const productsWithImages = await Promise.all(
        response.data.map(async (product) => {
          try {
            const imageResponse = await this.client.get(`/documents?modulepart=product&id=${product.id}&limit=100`);
            if (imageResponse.data && imageResponse.data.length > 0) {
              // Get the first image
              const imageDoc = imageResponse.data[0];
              // Use the share field to construct the public URL
              if (imageDoc.share) {
                product.image = `https://accounts.firehousespirits.store/document.php?hashp=${imageDoc.share}`;
              } else {
                // Fallback: construct URL using direct path (shouldn't be needed with sharing enabled)
                product.image = `https://accounts.firehousespirits.store/documents/product/${product.ref}/${imageDoc.filename}`;
              }
            }
          } catch (imageError) {
            // Silently ignore image errors
            console.log(`No image found for product ${product.id}`);
          }
          return product;
        })
      );

      return productsWithImages;
    } catch (error) {
      console.error('Error fetching products:', error);
      throw error;
    }
  }

  async getProduct(id) {
    try {
      const response = await this.client.get(`/products/${id}`, {
        params: {
          includestockdata: 1
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching product:', error);
      throw error;
    }
  }

  // Proposals (Quotes/Orders)
  async createProposal(proposalData) {
    try {
      const response = await this.client.post('/proposals', proposalData);
      return response.data;
    } catch (error) {
      console.error('Error creating proposal:', error);
      throw error;
    }
  }

  async getProposal(id) {
    try {
      const response = await this.client.get(`/proposals/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching proposal:', error);
      throw error;
    }
  }

  async getProposalByRef(ref) {
    try {
      const response = await this.client.get(`/proposals/ref/${ref}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching proposal by ref:', error);
      throw error;
    }
  }

  async getInvoice(id) {
    try {
      const response = await this.client.get(`/invoices/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching invoice:', error);
      throw error;
    }
  }

  // Create invoice directly (not from proposal)
  async createInvoice(invoiceData) {
    try {
      const response = await this.client.post('/invoices', invoiceData);
      return response.data;
    } catch (error) {
      console.error('Error creating invoice:', error);
      throw error;
    }
  }

  // Add payment to invoice
  async addInvoicePayment(invoiceId, paymentData) {
    try {
      const response = await this.client.post(`/invoices/${invoiceId}/payments`, paymentData);
      return response.data;
    } catch (error) {
      console.error('Error adding payment to invoice:', error);
      throw error;
    }
  }

  // Validate invoice
  async validateInvoice(invoiceId, validateData = {}) {
    try {
      const response = await this.client.post(`/invoices/${invoiceId}/validate`, {
        idwarehouse: 0,
        notrigger: 0,
        ...validateData
      });
      return response.data;
    } catch (error) {
      console.error('Error validating invoice:', error);
      throw error;
    }
  }

  // Third parties (Customers)
  async createThirdParty(thirdPartyData) {
    try {
      const response = await this.client.post('/thirdparties', thirdPartyData);
      return response.data;
    } catch (error) {
      console.error('Error creating third party:', error);
      throw error;
    }
  }

  async getThirdParty(id) {
    try {
      const response = await this.client.get(`/thirdparties/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching third party:', error);
      throw error;
    }
  }

  async searchThirdParties(searchParams) {
    try {
      const response = await this.client.get('/thirdparties', {
        params: {
          limit: 100,
          ...searchParams
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error searching third parties:', error);
      throw error;
    }
  }

  // Login
  async login(username, password) {
    try {
      const response = await this.client.get('/login', {
        params: {
          login: username,
          password: password
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error logging in:', error);
      throw error;
    }
  }

  // Stock movements
  async createStockMovement(movementData) {
    try {
      const response = await this.client.post('/stockmovements', movementData);
      return response.data;
    } catch (error) {
      console.error('Error creating stock movement:', error);
      throw error;
    }
  }
}

export default new DolibarrAPI();
